import { useRef, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Palette } from "lucide-react";
import { useBranding, resolveAssetUrl } from "@/contexts/BrandingContext";
import { BrandLogo } from "@/components/BrandLogo";

const LOGIN_FIELDS = [
  ["login_headline", "Headline", "Trusted training for"],
  ["login_highlight", "Headline highlight (coloured)", "brighter futures."],
  ["login_pillars", "Pillars (separate with |)", "Learn | Assess | Progress"],
  ["login_tagline", "Handwritten tagline", "People. Progress. Possibilities."],
  ["login_photo_url", "Background photo URL", "https://…"],
];

const DEFAULT_FEATURES = [{ title: "", text: "" }, { title: "", text: "" }, { title: "", text: "" }];

const FIELDS = [
  ["short_name", "Short name (sidebar switch, AI assistant)", "e.g. NTS"],
  ["lms_name", "LMS / Assessment portal name", "e.g. Training 4 Retrofit"],
  ["lms_short_name", "LMS short name (workspace switch)", "e.g. Retrofit"],
  ["sender_email", "Email sender address (verified in Postmark)", "noreply@yourcompany.co.uk"],
  ["sender_name", "Email sender name", "Your Company"],
  ["certificate_signatory_name", "Certificate signatory name", "e.g. Jane Smith"],
  ["certificate_signatory_title", "Certificate signatory title", "e.g. Managing Director"],
];

export const BrandingSettings = ({ company, setCompany, disabled }) => {
  const { refresh } = useBranding();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCompany((c) => ({ ...c, [k]: v }));
  const features = [0, 1, 2].map((i) => ({ ...DEFAULT_FEATURES[i], ...((company.login_features || [])[i] || {}) }));
  const setFeature = (i, k, v) => set("login_features", features.map((f, j) => (j === i ? { ...f, [k]: v } : f)));

  const save = async () => {
    setSaving(true);
    try {
      const keys = ["short_name", "lms_name", "lms_short_name", "sender_email", "sender_name", "certificate_signatory_name", "certificate_signatory_title", "brand_color", "default_course_location", ...LOGIN_FIELDS.map(([k]) => k)];
      await api.put("/settings/company", { ...Object.fromEntries(keys.map((k) => [k, company[k] ?? ""])), login_features: features });
      await refresh?.(); toast.success("Branding saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const darkRef = useRef(null);
  const upload = async (e, variant = "") => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("variant", variant);
    try { const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); Object.entries(data).forEach(([k, v]) => k.endsWith("_url") && set(k, v)); await refresh?.(); toast.success("Logo updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Logo upload failed"); }
  };

  return (
    <div className="bg-white rounded-lg border border-border p-5 space-y-4" data-testid="branding-settings">
      <h3 className="font-heading font-semibold flex items-center gap-2"><Palette size={16} /> Branding</h3>
      <div className="flex flex-wrap items-center gap-4">
        <div className="rounded-md border border-dashed border-border px-4 py-3 bg-slate-50"><BrandLogo size="lg" /></div>
        {!disabled && <Button size="sm" variant="outline" className="gap-1" onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload"><Upload size={12} /> Upload logo</Button>}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e)} data-testid="branding-logo-input" />
        <div className="rounded-md bg-[#06142e] px-4 py-3">{company.logo_dark_url ? <img src={resolveAssetUrl(company.logo_dark_url)} alt="Logo for dark backgrounds" className="h-12 object-contain" data-testid="branding-dark-logo-preview" /> : <span className="text-xs text-slate-300">No dark-background logo</span>}</div>
        {!disabled && <Button size="sm" variant="outline" className="gap-1" onClick={() => darkRef.current?.click()} data-testid="branding-dark-logo-upload"><Upload size={12} /> Logo for dark backgrounds</Button>}
        <input ref={darkRef} type="file" accept="image/png,image/webp" className="hidden" onChange={(e) => upload(e, "dark")} data-testid="branding-dark-logo-input" />
        <div className="flex items-center gap-2">
          <Label className="text-xs">Brand colour</Label>
          <input type="color" value={company.brand_color || "#0F172A"} onChange={(e) => set("brand_color", e.target.value)} disabled={disabled} className="h-8 w-12 cursor-pointer rounded border border-border" data-testid="branding-color" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k}><Label className="text-xs">{label}</Label><Input value={company[k] || ""} placeholder={ph} disabled={disabled} onChange={(e) => set(k, e.target.value)} data-testid={`branding-${k}`} /></div>
        ))}
      </div>
      <div><Label className="text-xs">Default course location</Label><Textarea value={company.default_course_location || ""} disabled={disabled} onChange={(e) => set("default_course_location", e.target.value)} className="min-h-[60px]" data-testid="branding-default_course_location" /></div>
      <div className="border-t border-border pt-4 space-y-3" data-testid="branding-login-section">
        <h4 className="text-sm font-semibold">Login page</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {LOGIN_FIELDS.map(([k, label, ph]) => (
            <div key={k}><Label className="text-xs">{label}</Label><Input value={company[k] || ""} placeholder={ph} disabled={disabled} onChange={(e) => set(k, e.target.value)} data-testid={`branding-${k}`} /></div>
          ))}
        </div>
        <div><Label className="text-xs">Intro text</Label><Textarea value={company.login_subtext || ""} disabled={disabled} onChange={(e) => set("login_subtext", e.target.value)} className="min-h-[60px]" data-testid="branding-login_subtext" /></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {features.map((f, i) => (
            <div key={i} className="space-y-1.5 rounded-md border border-border p-2">
              <Label className="text-xs">Feature {i + 1}</Label>
              <Input value={f.title} placeholder="Title" disabled={disabled} onChange={(e) => setFeature(i, "title", e.target.value)} data-testid={`branding-feature-title-${i}`} />
              <Input value={f.text} placeholder="Short description" disabled={disabled} onChange={(e) => setFeature(i, "text", e.target.value)} data-testid={`branding-feature-text-${i}`} />
            </div>
          ))}
        </div>
      </div>
      {!disabled && <Button className="text-white" style={{ backgroundColor: company.brand_color || "#0F172A" }} disabled={saving} onClick={save} data-testid="branding-save">Save branding</Button>}
    </div>
  );
};
